import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO } from '../data/mock';
import { ShieldCheck } from 'lucide-react';

const PrivacyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Tiny Wonders Pick';
  }, []);

  const corp = CONTACT_INFO.corpName;
  const domain = CONTACT_INFO.domain;
  const phone = CONTACT_INFO.phone;
  const email = CONTACT_INFO.email;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Banner */}
        <section className="bg-[#fdf4f7] border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-athletic-200 rounded-full mb-6">
              <ShieldCheck className="w-3.5 h-3.5 text-athletic-500" />
              <span className="text-athletic-700 text-[11px] font-medium tracking-[0.18em] uppercase">Privacy</span>
            </div>
            <h1 className="text-5xl lg:text-6xl text-[#1a1a1a] mb-4"
                style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 500, letterSpacing: '-0.02em' }}>
              Privacy <span className="italic text-athletic-500">Policy</span>
            </h1>
            <p className="text-[#1a1a1a]/65 max-w-xl leading-relaxed">
              How we collect, use and protect the personal information you share with us.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <p className="text-[#1a1a1a]/60 text-sm mb-2">
              <span className="font-medium text-[#1a1a1a]">Effective date:</span>{' '}
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>

            <p className="text-[#1a1a1a]/75 leading-relaxed text-base mb-12">
              This Privacy Policy (the &ldquo;Policy&rdquo;) discloses the practices of {corp} concerning personal information we
              obtain about you. By using <span className="text-athletic-600 font-medium">{domain}</span>, in any way accessing
              {' '}{domain}, or using other services of {corp} (collectively, the &ldquo;Services&rdquo;), you are accepting the
              practices described in this Policy. This Policy notifies you of: (a) what personal information of yours is collected
              when you access {domain} and/or use the Services or purchase any product; (b) how the information is collected; (c)
              how the information is used and shared; (d) our efforts to maintain the security of Sensitive Information; and (e)
              other details concerning your personal information and our policies. This Policy does not apply to the practices of
              companies that {corp} does not own or control, or to people that we do not employ or manage.
            </p>

            <Section title="Information Collection and Use">
              <p>
                Information collected by {corp} is stored in a single, secure database, accessible only by {corp}. {corp} collects
                information from our paying customers or other persons who use, attempt to use, or purchase {corp} products or
                services at several different points and from different sources, all or some of which may apply to you.
              </p>

              <SubSection title="Registration">
                <p>
                  When you sign up as a customer or potential customer, we ask for, and you are required to give, your name, address,
                  zip code, telephone number, email address, credit card or checking account number, and credit card expiration date.
                  Once you register as a customer with us, you are not anonymous to us.
                </p>
              </SubSection>

              <SubSection title="Cookies">
                <p>
                  Cookies are alphanumeric identifiers that we transfer to your computer&rsquo;s hard drive through your web browser
                  to enable our systems to recognize your browser and to provide features such as cart persistence and personalized
                  content.
                </p>
              </SubSection>

              <SubSection title="Log Files">
                <p>
                  {corp} automatically receives and records information on its server logs from your web browser, including your IP
                  address, cookie information, and the page you requested. We use IP addresses and cookies to analyze trends,
                  administer the site, track members and users movements, and gather broad demographic information for aggregate use.
                  IP addresses are not linked to your personal information.
                </p>
              </SubSection>

              <SubSection title="Information from Other Sources">
                <p>
                  For reasons such as improving personalization of our Service (for example, providing better product recommendations
                  or special offers that we think will interest you), we might receive information about you from other sources and
                  add it to our account information. We also sometimes receive updated delivery and address information from our
                  shippers or other sources so that we can correct our records and deliver your next purchase or communication more
                  easily.
                </p>
              </SubSection>
            </Section>

            <Section title="Use of Information">
              <p>Information we collect is generally used for the following purposes:</p>
              <ol className="list-decimal pl-6 my-4 space-y-2">
                <li>
                  To build features that will make the services available on our site easier to use. This includes faster search
                  requests, better member support and timely notice of new services and special offers.
                </li>
                <li>
                  To improve our marketing and promotional efforts, to analyze site usage, improve our content and product offerings,
                  and customize our site&rsquo;s content, layout, and services. These uses improve our Services and better tailor
                  them to meet your needs.
                </li>
                <li>
                  To resolve disputes, troubleshoot problems and enforce our Terms and Conditions of Use. At times, we may look
                  across multiple members or users to identify problems or resolve disputes, and in particular we may examine your
                  information to identify members using multiple Member IDs or aliases.
                </li>
              </ol>
              <p>
                We reserve the right to disclose personal information, including Sensitive Information, when we believe that such
                disclosure is appropriate to comply with the law or a request by a government official, to protect the rights or
                property of {corp}, or to enforce our Terms and Conditions of Use. In the event that {corp} or substantially all of
                its assets are acquired by a third party, member information and Sensitive Information may be some of the transferred
                assets.
              </p>
            </Section>

            <Section title="Use of Data Collected">
              <p>
                We do not share customer card data with third parties. We use your personal, demographic and profile data to enhance
                your experience at our site and to enable us to present content we think you might be interested in. We use your
                contact information to send you information about {corp} and promotional material from our partners. We may also use
                your personal, demographic and profile data to improve our site, for statistical analysis, for marketing and
                promotional purposes, to send SMS alerts or notifications, and for editorial or feedback purposes for our advertisers.
                Information collected by us may be added to our databases and used for future telemarketing, SMS text messaging,
                emails or postal mailings regarding site updates, new products and services, upcoming events, and/or status of orders
                placed online. By using this site, you agree that you may be contacted in any manner contemplated in this section
                even if your number is found on a do-not-call registry, in-house list or similar registry.
              </p>
            </Section>

            <Section title="SMS Offering and Opt-out Rights">
              <p>
                By completing or submitting a registration form or partial registration form, you are consenting to receive SMS,
                wireless or other mobile offerings to your cell phone. You understand that your wireless carrier&rsquo;s standard
                charges and rates apply to these messages. For SMS text messages, you may remove your information by replying
                {' '}<span className="font-medium text-[#1a1a1a]">STOP</span>,{' '}
                <span className="font-medium text-[#1a1a1a]">END</span>, or{' '}
                <span className="font-medium text-[#1a1a1a]">QUIT</span> to the SMS text message you have received, and we will
                remove your personal information within 10 days of receiving such request.
              </p>
            </Section>

            <Section title="Agents">
              <p>
                We employ other companies and individuals to perform functions on our behalf. Examples include fulfilling orders,
                delivering packages, sending postal mail and email, removing repetitive information from member lists, analyzing
                data, providing marketing assistance, processing credit card payments, and providing member services. They have
                access to personal and Sensitive Information needed to perform their functions, but may not use it for other
                purposes.
              </p>
            </Section>

            <Section title="Special Offers and Announcements">
              <p>
                Active customers and former customers will occasionally receive information on products, services, special deals, and
                a newsletter.
              </p>
            </Section>

            <Section title="Security">
              <p>
                {corp} takes every precaution to protect our users and members Sensitive Information, both online and offline. Your
                Sensitive Information is password-protected for your privacy and security. Sensitive Information is encrypted and is
                protected online with the best encryption software in the industry &mdash; SSL. Sensitive Information is also
                protected offline in our offices. Only employees who need the information to perform a specific job (for example, a
                billing clerk or member services representative) are granted access to Sensitive Information. Furthermore, all
                employees are kept up to date on our security practices and changes in those practices. Finally, the servers that we
                store Sensitive Information on are kept in a secure environment. If you have any questions about the security at
                {' '}{domain}, you can send an email to us at{' '}
                <a href={`mailto:${email}`} className="text-athletic-600 hover:text-athletic-700 font-medium">{email}</a>.
              </p>
            </Section>

            <Section title="Other Sites">
              <p>
                {corp} forwards users and members to other sites. Please be aware that {corp} is not responsible for the business and
                privacy practices of these other sites. We encourage you to be aware of this when you leave our site and to read the
                legal notices and privacy policies of each and every website you visit.
              </p>
            </Section>

            <Section title="Correction / Updating Personal Information">
              <p>
                If a member&rsquo;s personal information changes we will endeavor to provide a way to correct or update that
                member&rsquo;s personal data provided to us. Please contact our Customer Care team at{' '}
                <a href={`mailto:${email}`} className="text-athletic-600 hover:text-athletic-700 font-medium">{email}</a>{' '}
                to update.
              </p>
            </Section>

            <Section title="Notification of Changes">
              <p>
                If we decide to change our Policy, we will post these changes on our homepage or provide other notification of our
                revised Policy so our users and members are always aware of what information we collect, how we use it, and when we
                disclose it.
              </p>
            </Section>

            <Section title="Children's Online Privacy Protection">
              <p>
                {corp} serves general users of the World Wide Web. We support and comply with the Children&rsquo;s Online Privacy
                Protection Act (COPPA) and we do not knowingly collect information from children under the age of 13, nor do we
                share such information with third parties. Children under the age of 18 may use {domain} only with the involvement of
                a parent or guardian.
              </p>
            </Section>

            <Section title="Special Notification for California Residents — Your California Privacy Rights">
              <p>
                Individual customers who reside in California and have provided their personal information to {corp} may request
                information about our disclosures of certain categories of personal information to third parties for their direct
                marketing purposes. Such requests must be submitted to us by emailing{' '}
                <a href={`mailto:${email}`} className="text-athletic-600 hover:text-athletic-700 font-medium">{email}</a>{' '}
                or by writing to {corp} at the address listed in our footer. Within thirty (30) days of receiving such a request,
                we will provide a list of the categories of personal information disclosed to third parties for third-party direct
                marketing purposes during the immediately preceding calendar year, along with the names and addresses of these third
                parties. This request may be made no more than once per calendar year. We reserve our right not to respond to
                requests submitted other than to the address specified in this paragraph.
              </p>
            </Section>

            <div className="mt-16 pt-8 border-t border-gray-100">
              <p className="text-sm text-[#1a1a1a]/50">
                Questions about this Policy? Contact us at{' '}
                <a href={`mailto:${email}`} className="text-athletic-600 hover:text-athletic-700 font-medium">{email}</a>{' '}
                or {phone}.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mt-12 pt-8 border-t border-gray-100 first:border-t-0 first:pt-0 first:mt-0">
    <h2 className="text-2xl lg:text-3xl text-[#1a1a1a] mb-5"
        style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 600 }}>
      {title}
    </h2>
    <div className="text-[#1a1a1a]/75 leading-relaxed space-y-3 text-base">
      {children}
    </div>
  </section>
);

const SubSection = ({ title, children }) => (
  <div className="mt-6">
    <h3 className="text-lg text-athletic-600 mb-2 font-medium tracking-wide uppercase text-xs"
        style={{ letterSpacing: '0.18em' }}>
      {title}
    </h3>
    <div className="text-[#1a1a1a]/75 leading-relaxed">
      {children}
    </div>
  </div>
);

export default PrivacyPage;
